import React, { useEffect, useRef, useCallback, memo, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { defaultStyle, getGeometryFromLayer } from './mapUtils';
import MapProviderSelector from './MapProviderSelector';

// Define styles for map elements - only what's needed for polygon functionality
const mapStyles = {
  finalPolygon: {
    color: '#ff3333', 
    weight: 3, 
    opacity: 0.8, 
    fillColor: '#ff5555', 
    fillOpacity: 0.3
  },
  propertyLine: {
    color: '#4caf50',
    weight: 3,
    opacity: 0.8,
    fillColor: '#4caf50',
    fillOpacity: 0.1,
  }
};

// Component styles
const useStyles = makeStyles((theme) => ({
  mapContainer: {
    height: '75vh', 
    minHeight: 600, 
    width: '100%',
    border: '1px solid #ccc', 
    borderRadius: 4,
    position: 'relative', 
    overflow: 'hidden', 
    zIndex: 1,
    backgroundColor: '#f5f5f5'
  },
  drawingModeIndicator: {
    position: 'absolute', 
    top: 10, 
    left: '50%', 
    transform: 'translateX(-50%)', 
    zIndex: 1001, 
    background: 'rgba(0,0,0,0.7)', 
    color: 'white', 
    padding: '4px 10px', 
    borderRadius: '4px', 
    fontSize: '0.9rem', 
    pointerEvents: 'none',
    fontWeight: 'bold',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1002,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  providerSelector: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 1000,
  }
}));

function preventOutline(layer) {
  if (layer._path) {
    layer._path.setAttribute('tabindex', '-1');
    layer._path.setAttribute('focusable', 'false');
    L.DomUtil.addClass(layer._path, 'no-outline');
  }
}

// Setup custom vertex-only editing - this is the key function to remove outlines
const setupVertexOnlyEditing = () => {
  console.log('setupVertexOnlyEditing')
  // Add CSS for vertices
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = `
    .leaflet-vertex-icon {
      border-radius: 50%;
      border: 2px solid #1976d2;
      background-color: white;
      width: 10px !important;
      height: 10px !important;
      margin-left: -5px !important;
      margin-top: -5px !important;
    }
    .leaflet-midpoint-icon {
      border-radius: 50%;
      border: 2px solid #1976d2;
      background-color: white;
      opacity: 0.7;
      width: 8px !important;
      height: 8px !important;
      margin-left: -4px !important;
      margin-top: -4px !important;
    }
    
    /* Hide the guide lines completely - this is critical */
    .leaflet-edit-line-guide {
      display: none !important;
    }
    
    /* Ensure no editing-related outline shows up */
    .leaflet-editing-icon {
      opacity: 1 !important;
    }
    .leaflet-edit-move {
      display: none !important;
    }
    .leaflet-edit-resize {
      display: none !important;
    }
    .leaflet-poly-edited {
      border: none !important;
      outline: none !important;
    }
    .leaflet-edit-line-guide {
      opacity: 0 !important;
      display: none !important;
      visibility: hidden !important;
    }
  `;
  style.innerHTML += `
  /* Remove blue selection outline */
  .leaflet-interactive {
    outline: none !important;
    outline-offset: 0 !important;
    stroke: inherit !important; /* prevent browser-styled focus color */
  }
  
  .leaflet-interactive:focus {
    outline: none !important;
    outline-offset: 0 !important;
    stroke: inherit !important;
  }
  
  path:focus {
    outline: none !important;
    stroke: inherit !important;
  }
  
`;
  // Only add if not already present
  if (!document.querySelector('style[data-vertex-styling="true"]')) {
    style.setAttribute('data-vertex-styling', 'true');
    document.getElementsByTagName('head')[0].appendChild(style);
  }
  
  // Only patch Leaflet.Edit.Poly prototype if it exists and hasn't been patched
  if (L.Edit && L.Edit.Poly && !L.Edit.Poly._vertexOnlyEditingApplied) {
    // Store original methods
    const originalCreateMarker = L.Edit.Poly.prototype._createMarker;
    const originalCreateMidpointMarker = L.Edit.Poly.prototype._createMidpointMarker;
    
    // Override marker creation for vertices
    L.Edit.Poly.prototype._createMarker = function(latlng, index) {
      const marker = originalCreateMarker.call(this, latlng, index);
      marker.setIcon(L.divIcon({
        className: 'leaflet-vertex-icon',
        iconSize: [10, 10]
      }));
      return marker;
    };
    
    // Override midpoint marker creation
    L.Edit.Poly.prototype._createMidpointMarker = function(marker1, marker2) {
      const midpointMarker = originalCreateMidpointMarker.call(this, marker1, marker2);
      midpointMarker.setIcon(L.divIcon({
        className: 'leaflet-midpoint-icon',
        iconSize: [8, 8]
      }));
      return midpointMarker;
    };
    
    // This is the critical part - completely disable guide lines
    L.Edit.Poly.prototype._updateGuides = function() {
      console.log("Guide lines prevented from drawing");
      // Do nothing - prevents guide lines from being drawn
      if (this._map && this._map._pathRoot && this._poly._map) {
        // Do minimal calculations to keep projection working
        const map = this._poly._map;
        
        // Clear existing guides if any - do NOT remove this
        this._clearGuides();
        
        // We're not drawing the guide lines, but we still need
        // to maintain the guides array for internal tracking
        this._guides = [];
        
        // Still calculate the markers to ensure coordinates stay valid
        for (let i = 0, l = this._markers.length; i < l - 1; i++) {
          // Just populate the guides array but don't actually draw them
          const marker1 = this._markers[i];
          const marker2 = this._markers[i + 1];
          
          // Skip creating visible guides, but maintain required references
          if (marker1._index !== undefined && marker2._index !== undefined) {
            this._guides.push(null); // Maintain array structure without actual guides
          }
        }
      }
    };
    
    // Mark as patched
    L.Edit.Poly._vertexOnlyEditingApplied = true;
  }
};

const MapComponent = ({
    provider,
    drawings = [],
    polygons = [],
    initialCenter = [33.55, -112.0],
    initialZoom = 12,
    center,
    zoom,
    drawingMode = 'polygon',
    onMapReady,
    onDrawCreated,
    onDrawEdited,
    onDrawDeleted,
    onDrawStart,
    onDrawStop,
    onMapViewChange,
    isDrawingToolActive,
    providers,
    onProviderChange,
    loading,
    loadingMessage,
    onPolygonEdited,
    currentlyEditingId,
    propertyLines,
    // setCurrentlyEditingId,
    selectDrawing,
    onGeofenceRadiusChange,
    onGeofenceMoved,
    geofenceCircle,
    onGeofenceEditingChanged,
    activeTab,
    manualPropertyLines,
    onDrawingGeometryChanged
  }) => {
    console.log('Map Component:: Polygons', polygons)
    console.log('Map drawings, drawings)', drawings)
    console.log('geofenceCircle...', geofenceCircle)
    console.log('ActiveTab', activeTab)
 


    // console.log("MapComponent that never changes...got currentlyEditingId:", currentlyEditingId);

    const classes = useStyles();
    
    const [isInitialized, setIsInitialized] = useState(false);

    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const baseLayerRef = useRef(null);
    const drawnItemsRef = useRef(null);
    const drawControlRef = useRef(null);
    const polygonsLayerRef = useRef(null);
    const propertyLinesRef = useRef(null);

    // const currentlyEditingIdRef = useRef(null);

    // // console.log('Do I ever')
    useEffect(() => {
      console.log('currentlyEditingId changed....', currentlyEditingId)
    //   currentlyEditingIdRef.current = currentlyEditingId;
    }, [currentlyEditingId]);

    useEffect(() => {
      if (!geofenceCircle || !mapInstanceRef.current) return;
      
      try {
        // Only show geofence circle on tab 0 (Measure Property)
        if (activeTab === 0) {
          // Make sure circle is on the map if it should be visible
          if (!geofenceCircle._map) {
            geofenceCircle.addTo(mapInstanceRef.current);
            
            // Also add to drawn items layer for consistency
            if (drawnItemsRef.current && !drawnItemsRef.current.hasLayer(geofenceCircle)) {
              drawnItemsRef.current.addLayer(geofenceCircle);
            }
          }
        } else {
          // Remove circle from map when not on tab 0
          if (geofenceCircle._map) {
            geofenceCircle.remove();
          }
          
          // Also remove from drawn items layer if present
          if (drawnItemsRef.current && drawnItemsRef.current.hasLayer(geofenceCircle)) {
            drawnItemsRef.current.removeLayer(geofenceCircle);
          }
        }
      } catch (error) {
        console.error('Error toggling geofence visibility:', error);
      }
    }, [activeTab, geofenceCircle, mapInstanceRef, drawnItemsRef]);


    useEffect(() => {
      // if (prop)
      console.log('propertyLines on map component...', propertyLines)
    }, [propertyLines])

    useEffect(() => {
      console.log('Manual....manualPropertyLines', manualPropertyLines)
    }, [manualPropertyLines])

    useEffect(() => {
      console.log('308 property lines changed......')
      if (!mapInstanceRef.current || !propertyLinesRef.current || !propertyLines) return;
    
      propertyLinesRef.current.clearLayers();
      console.log('Add property lines to our ref...', propertyLines)
    
      propertyLines.forEach((line, index) => {
        if (!line.paths || line.paths.length === 0) return;
        if (line.isHidden) {
          console.log('hiddne line...', line)
          return
        }
        try {
          const style = mapStyles.propertyLine;
          const layer = L.polygon(line.paths, style);
    
          // Optional: add static tooltip
          if (line.name || line.APN) {
            layer.bindTooltip(`${line.APN || ''} ${line.name || ''}`);
          }
    
          // No interactivity
          layer.off(); // just in case
          layer.options.interactive = false;
    
          propertyLinesRef.current.addLayer(layer);
        } catch (error) {
          console.error(`MapComponent: Error rendering property line [${index}]:`, error);
        }
      });
    }, [propertyLines]);

    console.log('298 mapInstanceRef', mapInstanceRef)
    console.log('propertyLinesRef....', propertyLinesRef)
    useEffect(() => {
      if (!geofenceCircle || !mapInstanceRef.current || !isInitialized) return;
      
      try {
        // Only proceed with attaching if we're on tab 0
        if (activeTab !== 0) return;
        
        // Ensure the circle has editing capabilities
        if (!geofenceCircle.editing) {
          geofenceCircle.editing = new L.Edit.Circle(geofenceCircle, {
            moveIcon: new L.DivIcon({
              iconSize: new L.Point(8, 8),
              className: 'leaflet-div-icon leaflet-editing-icon'
            }),
            resizeIcon: new L.DivIcon({
              iconSize: new L.Point(8, 8),
              className: 'leaflet-div-icon leaflet-editing-icon leaflet-edit-resize'
            })
          });
        }
        
        // Add the circle to map if not already there
        if (!geofenceCircle._map) {
          geofenceCircle.addTo(mapInstanceRef.current);
          
          if (drawnItemsRef.current) {
            drawnItemsRef.current.addLayer(geofenceCircle);
          }
        }
        // Ensure handles are visible when editing
        const originalOnClick = geofenceCircle._events?.click?.[0]?.fn || null;
        geofenceCircle.off('click');

      geofenceCircle.on('click', (e) => {
        console.log('Geofence circle clicked');
        L.DomEvent.stopPropagation(e);
        e.originalEvent.preventDefault();
        
        // Enable editing and notify parent component
        if (geofenceCircle.editing) {
          geofenceCircle.editing.enable();
          // Notify that editing has started
          if (onGeofenceEditingChanged) {
            onGeofenceEditingChanged(true);
          }
        }
        
        // Notify about editing if needed
        if (selectDrawing && geofenceCircle._id) {

          selectDrawing(geofenceCircle._id);
        }
      });

      // Add event listeners for end of editing
      geofenceCircle.on('editend', () => {
        if (onGeofenceEditingChanged) {
          onGeofenceEditingChanged(false);
        }
      });

      // Add map click listener to detect when editing stops
      mapInstanceRef.current.on('click', () => {
        if (geofenceCircle.editing && geofenceCircle.editing.enabled()) {
          // First check if we're actually editing
          if (onGeofenceRadiusChange) {
            onGeofenceRadiusChange(geofenceCircle.getRadius());
          }
          if (onGeofenceEditingChanged) {
            onGeofenceEditingChanged(false);
          }
        }
      });


      // Add event listeners for end of editing
      geofenceCircle.on('editend', () => {
        if (onGeofenceEditingChanged) {
          onGeofenceEditingChanged(false);
        }
      });
        
        // Add CSS to ensure editing handles are visible
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = `
          .leaflet-editing-icon {
            border-radius: 50%;
            background-color: white;
            border: 2px solid #1976d2;
            width: 10px !important;
            height: 10px !important;
            margin-left: -5px !important;
            margin-top: -5px !important;
            display: block !important;
          }
          .leaflet-edit-resize, .leaflet-edit-move {
            display: block !important; 
          }
        `;
        
        if (!document.querySelector('style[data-circle-edit="true"]')) {
          style.setAttribute('data-circle-edit', 'true');
          document.getElementsByTagName('head')[0].appendChild(style);
        }
      } catch (error) {
        console.error('Error setting up circle editing:', error);
      }
      
      return () => {
        geofenceCircle.off('click');
      };
    }, [geofenceCircle, mapInstanceRef, isInitialized, drawnItemsRef, selectDrawing, activeTab]);

  console.log('Polygon to edit...', currentlyEditingId)
    // Setup custom edit handler before map initialization
    useEffect(() => {
      setupVertexOnlyEditing();
    }, []);
  
    // Function to add/update base layer (tile provider)
    const updateBaseLayer = useCallback((currentMap, providerToAdd) => {
      if (!currentMap || !providerToAdd) return;
      
      if (baseLayerRef.current) { 
        currentMap.removeLayer(baseLayerRef.current); 
        baseLayerRef.current = null; 
      }
      
      let newLayer;
      try {
        console.log('Setting new provider:', providerToAdd.name);
        
        if (providerToAdd.group === "maricopa") {
          const tileOptions = {
            attribution: providerToAdd.attribution,
            minZoom: providerToAdd.minZoom || 0,
            maxZoom: 25,
            maxNativeZoom: 21,
            errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
          };
  
          if (providerToAdd.isExportService && L.esri?.dynamicMapLayer) {
            newLayer = L.esri.dynamicMapLayer({
              url: providerToAdd.serviceUrl,
              opacity: 1.0,
              useCors: false,
              f: 'image',
              format: 'png32',
              transparent: true,
              maxNativeZoom: providerToAdd.maxZoom || 25,
              ...tileOptions
            });
          } else {
            newLayer = L.tileLayer(providerToAdd.url, { 
              ...tileOptions,
              minZoom: providerToAdd.minZoom, 
              maxZoom: providerToAdd.maxZoom 
            });
          }
        } else if (providerToAdd.group === "google") {
          newLayer = L.tileLayer(providerToAdd.url, { 
            attribution: providerToAdd.attribution, 
            minZoom: providerToAdd.minZoom || 0, 
            maxZoom: 25,
            maxNativeZoom: providerToAdd.maxZoom || 19,
            errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7' 
          });
        }
        
        if (newLayer) {
          newLayer.on('tileerror loaderror', (e) => 
            console.error(`MapComponent: Layer Error for ${providerToAdd.name}`, e)
          );
          newLayer.addTo(currentMap);
          baseLayerRef.current = newLayer;
        }
      } catch (error) { 
        console.error(`MapComponent: Error adding layer ${providerToAdd?.name}:`, error); 
      }
    }, []);

    useEffect(() => {
      // Add circle editing CSS
      const style = document.createElement('style');
      style.type = 'text/css';
      style.innerHTML = `
        /* Make circle editing handles always visible */
        .leaflet-editing-circle {
          stroke-dasharray: none !important;
        }
        
        .leaflet-edit-resize, 
        .leaflet-edit-move {
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Style the resize handles */
        .leaflet-edit-resize {
          border: 2px solid #2196F3;
          background-color: white;
          border-radius: 50%;
          width: 12px !important;
          height: 12px !important;
        }
        
        /* Style the move handle */
        .leaflet-edit-move {
          border: 2px solid #FF5722;
          background-color: white;
          border-radius: 50%;
          width: 12px !important;
          height: 12px !important;
        }
      `;
      
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    }, []);
  
    // Function to update displayed drawings (your manually created areas)
    const updateDisplayedDrawings = useCallback((featureGroup, drawingsToDisplay) => {
      if (!featureGroup) return;

      // Instead of clearing all layers, update existing ones and add/remove as needed
      const existingLayers = {};
      featureGroup.eachLayer(layer => {
        if (layer._drawingId) {
          existingLayers[layer._drawingId] = layer;
        }
      });

      // Track which drawings we've processed
      const processedIds = new Set();

      drawingsToDisplay.forEach(drawing => {
        let layer = existingLayers[drawing.id];
        processedIds.add(drawing.id);

        try {
          // Check if drawing is hidden and adjust style accordingly
          let style = { ...defaultStyle, ...(drawing.style || {}) };

          if (drawing.isHidden) {
            // Make the drawing invisible if hidden
            style = {
              ...style,
              fillOpacity: 0,
              opacity: 0
            };
          }

          // If layer exists, just update its style
          if (layer) {
            if (layer.setStyle) {
              layer.setStyle(style);
            }
            return;
          }

          // Otherwise create new layer
          if (drawing.geometry?.latlngs) {
            layer = L.polygon(drawing.geometry.latlngs, style);
          } else {
            return; // Skip non-polygon drawings
          }
          
          // Attach drawing ID to layer for reference
          layer._drawingId = drawing.id;
          
          // Add click handler to enable editing with vertices
          layer.on('click', (e) => {
            console.log('295 click...', drawing)
            // Stop propagation to prevent map click handler from firing
            L.DomEvent.stopPropagation(e);
            e.originalEvent.preventDefault()
            if (layer._path) {
              layer._path.setAttribute('stroke-opacity', style.opacity);
              layer._path.setAttribute('fill-opacity', style.fillOpacity);
              
              // Add a specific class we can target with CSS
              L.DomUtil.addClass(layer._path, 'editing-no-outline');
            }
            // Disable editing on all other layers
            featureGroup.eachLayer((otherLayer) => {
              if (otherLayer !== layer && otherLayer.editing && otherLayer.editing.enabled()) {
                otherLayer.editing.disable();
              }
            });
            
            // Also disable editing on polygon layers
            if (polygonsLayerRef.current) {
              polygonsLayerRef.current.eachLayer((otherLayer) => {
                if (otherLayer.editing && otherLayer.editing.enabled()) {
                  otherLayer.editing.disable();
                }
              });
            }
            
            // Enable editing on this layer
            if (layer.editing) {
              layer.editing.enable();
              // setCurrentlyEditingId(layer._drawingId);
              console.log('619 selectDrawing')
              selectDrawing(layer._drawingId)
              
            }
          });
          // layer._path.setAttribute('tabindex', '-1'); // Prevent path from getting focus
          // layer._path.setAttribute('focusable', 'false'); // SVG-specific
          // preventOutline(layer);

          featureGroup.addLayer(layer);
        } catch (e) {
          console.error("MapComponent: Error creating/updating layer for drawing:", drawing.id, e);
        }
      });

      // Remove layers that are no longer in the drawings list
      Object.keys(existingLayers).forEach(id => {
        if (!processedIds.has(id)) {
          featureGroup.removeLayer(existingLayers[id]);
        }
      });
    }, []);



    // Effect to initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    console.log("MapComponent: Initializing Leaflet map...");
    const map = L.map(mapContainerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      zoomControl: true,
      attributionControl: false
    });
    mapInstanceRef.current = map;
    L.control.attribution({ position: 'bottomright' }).addTo(map);

    propertyLinesRef.current = new L.FeatureGroup();
    map.addLayer(propertyLinesRef.current);

    // Initialize FeatureGroup for drawings (user-created polygons)
    drawnItemsRef.current = new L.FeatureGroup();
    map.addLayer(drawnItemsRef.current);
    
    // Initialize FeatureGroup for polygons (fetched areas and property lines)
    polygonsLayerRef.current = new L.FeatureGroup();
    map.addLayer(polygonsLayerRef.current);

    // Initialize L.Control.Draw with polygon-only options
    drawControlRef.current = new L.Control.Draw({
      edit: { 
        featureGroup: drawnItemsRef.current, 
        remove: true,
        poly: {
          allowIntersection: false,
          showArea: false,
          guidelineDistance: 0 // Helps reduce guidelines
        },
        edit: true,
        circle: {
          // Make sure circle editing is fully enabled
          removable: true,
          editable: true
        },
        polygon: {
          removable: true,
          editable: true
        }
      },
      draw: { 
        polygon: { 
          shapeOptions: defaultStyle,
          allowIntersection: false,
          showArea: false
        }, 
        polyline: false,
        rectangle: false,
        circle: false,
        // Enable circle for geofence drawing
        // circle: {
        //   shapeOptions: {
        //     color: '#3388ff',
        //     weight: 3, 
        //     opacity: 0.8,
        //     fillColor: '#3388ff',
        //     fillOpacity: 0.2
        //   },
        //   metric: true,
        //   showRadius: true,
        //   feet: false
        // },
        marker: false,
        circlemarker: false 
      }
    });
    map.addControl(drawControlRef.current);

    const enableCircleTool = () => {
      if (mapInstanceRef.current && drawControlRef.current) {
        // Disable any active handlers first
        Object.values(drawControlRef.current._toolbars.draw._modes).forEach(mode => {
          if (mode.handler && mode.handler.enabled()) {
            mode.handler.disable();
          }
        });
        
        // Enable the circle handler
        const circleHandler = drawControlRef.current._toolbars.draw._modes.circle.handler;
        if (circleHandler) {
          circleHandler.enable();
        }
      }
    };

    // Global map click handler to disable active editing
    map.on('click', (e) => {
      console.log('Map click...')
      // Save any currently editing layer's changes before disabling edit mode
      if (currentlyEditingId) {
        // Look in drawn items
        console.log('525')
        drawnItemsRef.current?.eachLayer((layer) => {
          if (layer._drawingId === currentlyEditingId && layer.editing && layer.editing.enabled()) {
            const newGeometry = getGeometryFromLayer(layer);
            if (newGeometry && onDrawEdited) {
              const editEvent = {
                layers: {
                  eachLayer: (callback) => callback(layer)
                }
              };
              onDrawEdited(editEvent);
            }
            layer.editing.disable();
          }
        });
        console.log('540')
        // Look in polygons layer
        polygonsLayerRef.current?.eachLayer((layer) => {
          if (layer._polygonId === currentlyEditingId && layer.editing && layer.editing.enabled()) {
            // Get the updated paths
            const newPaths = layer.getLatLngs()[0].map(ll => ({ lat: ll.lat, lng: ll.lng }));
            
            // Call the edit callback with the polygon's ID and new paths
            if (onPolygonEdited && layer._polygonId) {
              onPolygonEdited(layer._polygonId, newPaths);
            }
            layer.editing.disable();
          }
        });
        selectDrawing(null)
        // setCurrentlyEditingId(null);
      }
    });
      
    // Attach event listeners for drawing
    map.on(L.Draw.Event.CREATED, (event) => {
      const layer = event.layer;
      if (event.layerType === 'circle') {
        // This would only happen if a circle is newly created, which should be rare now
        drawnItemsRef.current.addLayer(layer);
        
        // Set up the circle for editing
        const radius = layer.getRadius();
        layer._geofenceRadius = radius;
        
        // Make editing easy by enabling it immediately after creation
        if (layer.editing) {
          // Enable editing right away
          setTimeout(() => {
            layer.editing.enable();
          }, 100);
        }
        
        // Set up click handler for future editing
        layer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          e.originalEvent.preventDefault();
          
          if (layer.editing) {
            layer.editing.enable();
          }
        });
        
        // Call the radius change callback
        if (onGeofenceRadiusChange) {
          onGeofenceRadiusChange(radius);
        }
        
        // Call the center position callback
        if (onGeofenceMoved) {
          onGeofenceMoved(layer.getLatLng());
        }
        
        // Call the general draw created callback
        onDrawCreated && onDrawCreated(event);
      } else {
        if (event.layerType === 'polygon') {
          // Add to the drawn items layer group
          drawnItemsRef.current.addLayer(layer);
          
          // If it's a circle, record the radius
          if (event.layerType === 'circle') {
            const radius = layer.getRadius();
            // Store radius with the layer
            layer._geofenceRadius = radius;
            
            // If a callback is provided for geofence updates, call it
            if (onGeofenceRadiusChange) {
              onGeofenceRadiusChange(radius);
            }
          }
          
          // Trigger onDrawCreated if provided
          onDrawCreated && onDrawCreated(event);
          
          // Enable editing on click
          layer.on('click', (e) => {
            // Similar click handling as polygon...
            L.DomEvent.stopPropagation(e);
            e.originalEvent.preventDefault();
            
            // Disable editing on other layers...
            
            // Enable editing on this layer
            if (layer.editing) {
              layer.editing.enable();
              selectDrawing(layer._drawingId);
              
            }
          });
        }
      }
    });

    map.on('editable:editing', (event) => {
      const layer = event.layer;
      if (layer && layer.getRadius) {
        const newRadius = layer.getRadius();
        if (onGeofenceRadiusChange) {
          onGeofenceRadiusChange(newRadius);
        }
      }
    });

    map.on('editable:dragend', (event) => {
      const layer = event.layer;
      if (layer && layer instanceof L.Circle) {
        // Get the new center position
        const newCenter = layer.getLatLng();
        console.log('Circle moved to:', newCenter);
        
        // Notify parent component about the move
        if (onGeofenceMoved) {
          onGeofenceMoved(newCenter);
        }
      }
    });
      
    // Standard drawing events
    map.on(L.Draw.Event.EDITED, (event) => {
      console.log('869....EDITED!!', event)
      event.layers.eachLayer((layer) => {
        if (layer instanceof L.Circle) {
          // Circle was edited - could be radius change or position change
          const newRadius = layer.getRadius();
          const newCenter = layer.getLatLng();
          
          console.log('Circle edited:', { 
            radius: newRadius,
            center: { lat: newCenter.lat, lng: newCenter.lng }
          });
          
          // Update both radius and center
          if (onGeofenceRadiusChange) {
            onGeofenceRadiusChange(newRadius);
          }
          
          if (onGeofenceMoved) {
            onGeofenceMoved(newCenter);
          }
        }
      });
      
      // Call the general edit callback
      onDrawEdited && onDrawEdited(event);
    });

    map.on('draw:editvertex', (e) => {
      console.log('Vertex being edited:', e);
      const layer = e.poly;
      if (!layer) return;
      let doIt = false
      if (doIt) return
      const drawingId = layer._drawingId || layer._polygonId;
      if (!drawingId) return;
      const newGeometry = getGeometryFromLayer(layer);
      if (!newGeometry) return;
      
      console.log('Layer edited:', drawingId, newGeometry);
      
      // Extract paths for polygons
      let newPaths = null;
      if (newGeometry.latlngs) {
        newPaths = newGeometry.latlngs.map(ll => ({
          lat: ll.lat,
          lng: ll.lng
        }));
      }
      console.log('Layer 922...', layer)
      // Call the appropriate callbacks with updated geometry
      if (layer._drawingId && onDrawingGeometryChanged) {
        onDrawingGeometryChanged(layer._drawingId, newGeometry);
      }
      
      if (layer._polygonId && onPolygonEdited && newPaths) {
        onPolygonEdited(layer._polygonId, newPaths);
      }
      onDrawEdited && onDrawEdited(e);
    });
    
    // Call the general edit callback
    // onDrawEdited && onDrawEdited(e);

    map.on(L.Draw.Event.DELETED, (event) => onDrawDeleted && onDrawDeleted(event));
    map.on('draw:drawstart', (event) => onDrawStart && onDrawStart(event));
    map.on('draw:drawstop', (event) => onDrawStop && onDrawStop(event));
    map.on('draw:editstart', (event) => onDrawStart && onDrawStart({ ...event, layerType: 'edit' }));
    map.on('draw:editstop', (event) => onDrawStop && onDrawStop({ ...event, layerType: 'edit' }));
    map.on('draw:deletestart', (event) => onDrawStart && onDrawStart({ ...event, layerType: 'delete' }));
    map.on('draw:deletestop', (event) => onDrawStop && onDrawStop({ ...event, layerType: 'delete' }));

    // Track map movements
    map.on('zoomend moveend', () => {
      if (onMapViewChange && mapInstanceRef.current) {
        const currentCenter = mapInstanceRef.current.getCenter();
        const currentZoom = mapInstanceRef.current.getZoom();
        const bounds = mapInstanceRef.current.getBounds();
        onMapViewChange({ center: currentCenter, zoom: currentZoom, bounds });
      }
    });
    map.on('editable:vertex:dragend', function(e) {
      console.log('912 drag end...', e)
      const layer = e.poly;
      if (layer) {
        console.log('Vertex edited on polygon:', layer._drawingId || layer._polygonId);
        const layerId = layer._drawingId || layer._polygonId
        console.log('959 layerId', layerId)
        // Capture the updated geometry
        const newGeometry = getGeometryFromLayer(layer);
        if (newGeometry) {
          // Find the ID to update
          const idToUpdate = layerId
          
          // If we have a callback for geometry changes, call it
          if (onDrawingGeometryChanged && idToUpdate) {
            onDrawingGeometryChanged(idToUpdate, newGeometry);
          }
          
          // If this is a polygon from the polygons array, use that callback
          if (onPolygonEdited && layerId) {
            console.log('Extract those fuckin paths!!!')
            // Extract paths in the expected format
            const newPaths = layer.getLatLngs()[0].map(ll => ({ 
              lat: ll.lat, 
              lng: ll.lng 
            }));
            onPolygonEdited(layerId, newPaths);
          }
        }
      }
    });

    const focusOnGeofenceCircle = (circle) => {
      if (!circle || !mapInstanceRef.current) return;
      
      try {
        // Enable editing
        if (circle.editing) {
          circle.editing.enable();
        }
        
        // Zoom to the circle
        mapInstanceRef.current.fitBounds(circle.getBounds(), { 
          padding: [50, 50],
          maxZoom: 18
        });
        
        // Highlight it somehow (optional)
        const originalStyle = { ...circle.options };
        circle.setStyle({ 
          weight: originalStyle.weight + 1,
          opacity: 1.0
        });
        
        // Reset style after a short delay
        setTimeout(() => {
          circle.setStyle(originalStyle);
        }, 1500);
      } catch (error) {
        console.error("Error focusing on geofence circle:", error);
      }
    };

    // Call ready callback with references
    if (onMapReady) { 
      onMapReady({ 
        map, 
        drawnItemsLayer: drawnItemsRef.current, 
        drawControlInstance: drawControlRef.current,
        polygonsLayer: polygonsLayerRef.current,
        focusOnGeofenceCircle
      }); 
    }

    // Add initial base layer & drawings
    updateBaseLayer(map, provider);
    updateDisplayedDrawings(drawnItemsRef.current, drawings);
    
    setIsInitialized(true);

    // Cleanup on unmount
    return () => {
      // Handle any active edits before unmounting
      if (currentlyEditingId) {
        drawnItemsRef.current?.eachLayer((layer) => {
          if (layer._drawingId === currentlyEditingId && layer.editing && layer.editing.enabled()) {
            const newGeometry = getGeometryFromLayer(layer);
            if (newGeometry && onDrawEdited) {
              const editEvent = {
                layers: {
                  eachLayer: (callback) => callback(layer)
                }
              };
              onDrawEdited(editEvent);
            }
          }
        });
        
        polygonsLayerRef.current?.eachLayer((layer) => {
          if (layer._polygonId === currentlyEditingId && layer.editing && layer.editing.enabled()) {
            const newPaths = layer.getLatLngs()[0].map(ll => ({ lat: ll.lat, lng: ll.lng }));
            if (onPolygonEdited) {
              onPolygonEdited(layer._polygonId, newPaths);
            }
          }
        });
      }
      
      console.log("MapComponent: Cleaning up map instance...");
      if (mapInstanceRef.current) { 
        mapInstanceRef.current.remove(); 
        mapInstanceRef.current = null; 
      }
      baseLayerRef.current = null;
      drawControlRef.current = null;
      drawnItemsRef.current = null;
      polygonsLayerRef.current = null;
    };

  }, [updateBaseLayer, updateDisplayedDrawings])


  useEffect(() => {
    console.log('Center/Zoom Changed:', initialCenter, initialZoom)
  }, [initialCenter, initialZoom])

  useEffect(() => {
    console.log('Use Effect Fired...')
  }, [
    initialCenter, 
    initialZoom, 
    updateBaseLayer, 
    updateDisplayedDrawings, 
    drawings, 
    provider, 
    onMapReady, 
    onDrawCreated, 
    onDrawEdited, 
    onDrawDeleted, 
    onDrawStart, 
    onDrawStop, 
    onMapViewChange,
    onPolygonEdited,
    currentlyEditingId,
    
  ]);

  const handleHighlightFromList = (id) => {
    let foundLayer = null;

    // Search in drawn items
    drawnItemsRef.current?.eachLayer(layer => {
      if (layer._drawingId === id) {
        foundLayer = layer;
      }
    });

    // Search in polygons
    polygonsLayerRef.current?.eachLayer(layer => {
      if (layer._polygonId === id) {
        foundLayer = layer;
      }
    });

    // If found, highlight/edit it
    if (foundLayer) {
      // Disable editing on other layers
      drawnItemsRef.current?.eachLayer(layer => {
        if (layer !== foundLayer && layer.editing?.enabled()) {
          layer.editing.disable();
        }
      });
      polygonsLayerRef.current?.eachLayer(layer => {
        if (layer !== foundLayer && layer.editing?.enabled()) {
          layer.editing.disable();
        }
      });

      // Enable editing on the selected one
      if (foundLayer.editing) {
        foundLayer.editing.enable();
        mapInstanceRef.current.fitBounds(foundLayer.getBounds(), { padding: [50, 50] });
      }
    }
  };

  // useEffect(() => {
  //   console.log(' changed 638...', currentlyEditingId)
  //   // handleHighlightFromList(currentlyEditingIdRef.current);
  // }, [currentlyEditingId]);

  useEffect(() => {
    if (!currentlyEditingId || !isInitialized) return;
  
    console.log('MapComponent: currentlyEditingId changed to', currentlyEditingId);
  
    // Wait one tick to ensure layers are rendered
    setTimeout(() => {
      handleHighlightFromList(currentlyEditingId);
    }, 50); // Even 0 or requestAnimationFrame could work
  }, [currentlyEditingId, isInitialized]);
  
  
  // Effect to update provider/base layer
  useEffect(() => {
    if (mapInstanceRef.current && provider) {
      console.log("MapComponent: Provider changed to:", provider.name);
      updateBaseLayer(mapInstanceRef.current, provider);
    }
  }, [provider, updateBaseLayer]);

  // Effect to update displayed drawings
  useEffect(() => {
    if (mapInstanceRef.current && drawnItemsRef.current) {
      updateDisplayedDrawings(drawnItemsRef.current, drawings);
    }
  }, [drawings, updateDisplayedDrawings]);

  // Handle external center/zoom changes
  useEffect(() => {
    // Only execute if map is initialized and center/zoom props have changed
    if (mapInstanceRef.current && isInitialized && (center || zoom)) {
      console.log("MapComponent: Updating view based on props:", { center, zoom });
      
      // If both center and zoom are provided
      if (center && zoom) {
        mapInstanceRef.current.setView(center, zoom, {
          animate: true,
          duration: 1.0 // 1 second animation
        });
      } 
      // If only center is provided
      else if (center) {
        mapInstanceRef.current.panTo(center, {
          animate: true,
          duration: 1.0
        });
      }
      // If only zoom is provided
      else if (zoom) {
        mapInstanceRef.current.setZoom(zoom, {
          animate: true
        });
      }
    }
  }, [center, zoom, isInitialized]);

  // Update the MapComponent's useEffect for handling polygon rendering
// Add this to the polygon rendering effect in MapComponent.js

  useEffect(() => {
    if (!mapInstanceRef.current || !isInitialized || !polygonsLayerRef.current) return;
    
    console.log("MapComponent: Rendering polygons from props:", polygons?.length || 0);
    
    // Clear existing polygons
    polygonsLayerRef.current.clearLayers();
    // const filteredPolygons = activeTab === 0 
    // ? polygons.filter(p => p.type !== 'geofence' && p.type !== 'circle')
    // : polygons;
    let filteredPolygons = polygons

    console.log('...filteredPolygons', filteredPolygons)
    if (activeTab === 0) {
      // Tab 0: Show everything except property lines (geofence, areas, etc.)
      filteredPolygons = polygons.filter(p => 
        p.type !== 'propertyLine' && 
        p.type !== 'manualPropertyLine' && 
        p.type !== 'temporaryPropertyLine'
      );
    } else if (activeTab === 2) {
      // Tab 2: Show ONLY property lines 
      filteredPolygons = polygons.filter(p => 
        p.type === 'propertyLine' || 
        p.type === 'manualPropertyLine' || 
        p.type === 'temporaryPropertyLine'
      );
    }
    console.log('Updated filtered polygons...', filteredPolygons)
    if (filteredPolygons && filteredPolygons.length > 0) {
      filteredPolygons.forEach(polygon => {
        if (polygon.isHidden) return;
        const isPropertyLine = polygon.type === 'propertyLine' || polygon.type === 'manualPropertyLine' || polygon.type === 'temporaryPropertyLine';
    
    // // Add each polygon to the feature group
    // if (polygons && polygons.length > 0) {
    //   polygons.forEach(polygon => {
    //     if (polygon.isHidden) return;
        
    //     // Handle both regular property lines and temporary (unsaved) property lines
    //     const isPropertyLine = polygon.type === 'propertyLine' || polygon.type === 'manualPropertyLine' || polygon.type === 'temporaryPropertyLine';
        
        if (isPropertyLine) {
          // For property lines, ensure we have paths
          if (!polygon.paths || polygon.paths.length === 0) return;
          if (polygon.isHidden) return
          try {
            // Determine style - slightly different for temporary property lines
            let style = mapStyles.propertyLine;
            
            // For temporary property lines (not yet saved), make them look slightly different
            if (polygon.type === 'temporaryPropertyLine' || polygon.type === 'manualPropertyLine') {
              style = {
                ...mapStyles.propertyLine,
                dashArray: '5, 5', // Add dashed line for unsaved property lines
                color: '#4caf50',
                fillColor: '#4caf50',
                opacity: 0.9,
                fillOpacity: 0.2
              };
            }
            
            // Apply custom colors if specified
            if (polygon.fillColor) style.fillColor = polygon.fillColor;
            if (polygon.strokeColor) style.color = polygon.strokeColor;
            if (polygon.style?.color) style.color = polygon.style.color;
            if (polygon.style?.fillColor) style.fillColor = polygon.style.fillColor;
            console.log('1244 Mapcomponent styles...', style)
            // Create the polygon layer
            const layer = L.polygon(polygon.paths, style);
            
            // Add tooltip with information if available
            const tooltipLabel = polygon.type === 'temporaryPropertyLine' || polygon.type === 'manualPropertyLine' 
              ? `${polygon.name || 'Property Line'} (Unsaved)` 
              : `${polygon.APN || ''} ${polygon.name || ''}`;
              
            layer.bindTooltip(tooltipLabel);
            
            // Store reference to original polygon data
            layer._polygonId = polygon.id;
            layer._polygonData = polygon;
            
            // Only make temporary/manual property lines editable
            if (polygon.type === 'temporaryPropertyLine' || polygon.type === 'manualPropertyLine') {
              // Add click handler to enable editing
              layer.on('click', (e) => {
                // Stop propagation to prevent map click handler from firing
                L.DomEvent.stopPropagation(e);
                e.originalEvent.preventDefault();
                
                // Disable editing on all other layers first
                polygonsLayerRef.current.eachLayer((otherLayer) => {
                  if (otherLayer !== layer && otherLayer.editing && otherLayer.editing.enabled()) {
                    otherLayer.editing.disable();
                  }
                });
                
                // Also disable editing on drawn items layers
                if (drawnItemsRef.current) {
                  drawnItemsRef.current.eachLayer((otherLayer) => {
                    if (otherLayer.editing && otherLayer.editing.enabled()) {
                      otherLayer.editing.disable();
                    }
                  });
                }
                
                // Enable editing on this layer
                if (layer.editing) {
                  layer.editing.enable();
                  selectDrawing(layer._polygonId);
                }
              });
            }
            
            // Add to feature group - ensure property lines are added first (at the bottom)
            polygonsLayerRef.current.addLayer(layer);
          } catch (error) {
            console.error(`MapComponent: Error rendering property line ${polygon.id}:`, error);
          }
        } else         if (polygon?.type !== 'geofence' && (!polygon.paths || polygon.paths.length === 0 || polygon.isHidden)) return;

        if (polygon.type === 'geofence' || polygon.type === 'circle') {
          try {
            // Make sure we have valid center and radius
            if (!polygon.center || !polygon.radius) {
              console.warn('Geofence circle missing center or radius:', polygon);
              return;
            }
            
            // Determine style for circle
            let style = {
              color: polygon.strokeColor || polygon.style?.color || '#3388ff',
              weight: polygon.style?.weight || 3,
              opacity: polygon.style?.opacity || 0.8,
              fillColor: polygon.fillColor || polygon.style?.fillColor || '#3388ff',
              fillOpacity: polygon.style?.fillOpacity || 0.2
            };
            
            // Create the circle layer
            const layer = L.circle([polygon.center.lat, polygon.center.lng], {
              radius: polygon.radius,
              ...style
            });
            
            // Add tooltip
            layer.bindTooltip(polygon.name || 'Geofence Circle');
            
            // Store reference to original data
            layer._polygonId = polygon.id;
            layer._polygonData = polygon;
            
            // Add click handler to enable editing
            layer.on('click', (e) => {
              L.DomEvent.stopPropagation(e);
              e.originalEvent.preventDefault();
              
              // Disable editing on all other layers first
              polygonsLayerRef.current.eachLayer((otherLayer) => {
                if (otherLayer !== layer && otherLayer.editing && otherLayer.editing.enabled()) {
                  otherLayer.editing.disable();
                }
              });
              
              // Also disable editing on drawn items layers
              if (drawnItemsRef.current) {
                drawnItemsRef.current.eachLayer((otherLayer) => {
                  if (otherLayer.editing && otherLayer.editing.enabled()) {
                    otherLayer.editing.disable();
                  }
                });
              }
              
              // Enable editing on this layer
              if (layer.editing) {
                layer.editing.enable();
                selectDrawing(layer._polygonId);
              }
            });
            
            // Handle radius changes during editing
            layer.on('edit', (e) => {
              if (onGeofenceRadiusChange) {
                onGeofenceRadiusChange(layer.getRadius());
              }
              
              if (onGeofenceMoved) {
                onGeofenceMoved(layer.getLatLng());
              }
            });
            
            // Add to feature group
            polygonsLayerRef.current.addLayer(layer);
            
          } catch (error) {
            console.error(`MapComponent: Error rendering geofence circle ${polygon.id}:`, error);
          }
        } else if (polygon.paths && polygon.paths.length > 0) {
          try {
            // Determine style based on polygon type
            let style;
            switch (polygon.type) {
              case 'propertyLine':
                style = mapStyles.propertyLine;
                break;
              case 'area':
              case 'polygon':
                style = mapStyles.finalPolygon;
                break;
              case 'subtraction':
                style = { ...mapStyles.finalPolygon, fillColor: '#ff9800', color: '#ff9800' };
                break;
              default:
                style = mapStyles.finalPolygon;
            }
            
            // Apply custom colors if specified
            if (polygon.fillColor) style.fillColor = polygon.fillColor;
            if (polygon.strokeColor) style.color = polygon.strokeColor;
            
            // Create the polygon layer
            const layer = L.polygon(polygon.paths, style);
            
            // Add tooltip with information if available
            if (polygon.name || polygon.APN) {
              layer.bindTooltip(`${polygon.APN || ''} ${polygon.name || ''}`);
            }
            
            // Store reference to original polygon data
            layer._polygonId = polygon.id;
            layer._polygonData = polygon;
            
            // Only make non-property-line polygons editable
            // if (polygon.type !== 'propertyLine') {
              // Add click handler to enable editing
              layer.on('click', (e) => {
                console.log('Polygon line click....', polygon)
                // Stop propagation to prevent map click handler from firing
                L.DomEvent.stopPropagation(e);
                e.originalEvent.preventDefault()
                // Disable editing on all other layers first
                polygonsLayerRef.current.eachLayer((otherLayer) => {
                  if (otherLayer !== layer && otherLayer.editing && otherLayer.editing.enabled()) {
                    otherLayer.editing.disable();
                  }
                });
                
                // Also disable editing on drawn items layers
                if (drawnItemsRef.current) {
                  drawnItemsRef.current.eachLayer((otherLayer) => {
                    if (otherLayer.editing && otherLayer.editing.enabled()) {
                      otherLayer.editing.disable();
                    }
                  });
                }
                console.log('What is the id??', layer)
                console.log('polygon id??', polygon?._id, polygon.id)
                // Enable editing on this layer
                if (layer.editing) {
                  layer.editing.enable();
                  // setCurrentlyEditingId(layer._polygonId);
                  selectDrawing(layer._polygonId)
                }
              });
            // }
            
            // Add to feature group
            polygonsLayerRef.current.addLayer(layer);
          } catch (error) {
            console.error(`MapComponent: Error rendering polygon ${polygon.id}:`, error);
          }
        } else {
          console.log('No valid paths...')
        }
      });
    }
  }, [polygons, isInitialized, activeTab, center, zoom, selectDrawing]);

  return (
    <Box 
      ref={mapContainerRef}
      className={classes.mapContainer}
    >
      {/* Drawing Mode Indicator */}
      {isDrawingToolActive && (
        <Box className={classes.drawingModeIndicator}>
          Drawing Mode Active
        </Box>
      )}
      
      {/* Loading Overlay */}
      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress size={48} color="primary" />
          <Typography variant="subtitle1" style={{ marginTop: 16 }}>
            {loadingMessage || 'Loading...'}
          </Typography>
        </Box>
      )}
      
      {/* Provider Selector */}
      {providers?.length > 0 && mapContainerRef.current && (
        <Box className={classes.providerSelector}>
          <MapProviderSelector
            providers={providers}
            selectedProvider={provider}
            onProviderChange={onProviderChange}
          />
        </Box>
      )}
    </Box>
  );
}

// export default MapComponent;
export default React.memo(MapComponent, (prevProps, nextProps) => {
  // Only re-render if these props actually change
  console.log('MapComponent nextProps..1519', nextProps)
  console.log('1520')
  console.log('Current tab -- %s   Next: %s  ', prevProps.activeTab, nextProps.activeTab)
  return (
    prevProps.provider?.name === nextProps.provider?.name &&
    prevProps.drawings === nextProps.drawings &&
    prevProps.polygons === nextProps.polygons &&
    // prevProps.center?.length === nextProps.center?.lat &&
    prevProps.center === nextProps.center &&
    prevProps.initialCenter === nextProps.initialCenter &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.isDrawingToolActive === nextProps.isDrawingToolActive &&
    prevProps.currentlyEditingId === nextProps.currentlyEditingId &&
    prevProps.activeTab === nextProps.activeTab && 
    prevProps.propertyLines === nextProps.propertyLines && 
    ((!prevProps.geofenceCircle && !nextProps.geofenceCircle) || 
    (prevProps.geofenceCircle === nextProps.geofenceCircle))
  );
});
